import { PaymentSchedule } from '../paymentSchedule/types'
import { PaymentSource, SavedPaymentMethod } from '../../payments/types'
import { CardBrand, PaymentMethodType, PaymentType, InitiatePayNowResponse, Money } from '@afterpay/types'
import { ConsumerAddress } from '../types'
import { Country } from '../../index'
import type { ConfirmPayNowRequest } from '../paymentSchedule'

export interface InitiateCustomAmountPaymentResponse extends InitiatePayNowResponse {}
export interface InitiateCustomAmountPaymentRequest {
  amount: Money
}

export interface InitiateCustomAmountPaymentQuery {
  orderId: number
  body: InitiateCustomAmountPaymentRequest
}

export interface ConfirmCustomPaymentPayNowQuery {
  orderId: number
  body: ConfirmPayNowRequest
}

export interface InitiateLendingPayoutQuery {
  orderId: number
}

export interface ToggleAutopayRequest {
  orderId: number
  enabled: boolean
}

export interface OrderMerchant {
  id: number
  name: string
  contactEmail: string
  contactNumber: string
  contactUrl?: string
  displayMerchantReference: boolean
  countryCode: Country
  merchantDisplayName?: string
  merchantLogoUrl?: string
  merchantAddress?: string
  agencyRef?: string
}

export enum OrderTransactionRefundStatus {
  NO_REFUNDS = 'NO_REFUNDS',
  PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED',
  FULLY_REFUNDED = 'FULLY_REFUNDED',
}

export interface OrderConsumer {
  email: string
  givenNames: string
  surname: string
  mobile: string
  address: ConsumerAddress
}

export enum OrderRefundStatus {
  CANCELLED,
  PENDING,
  CONFIRMED,
  COMPLETED,
}

export enum OrderPaymentResponsesType {
  DEBT_COLLECTION_AGENCY = 'DEBT_COLLECTION_AGENCY',
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
  PREPAID = 'PREPAID',
  UNRECOGNIZED = 'UNRECOGNIZED',
  ACH_BANK_ACCOUNT = 'ACH_BANK_ACCOUNT',
  CASH_APP_PAY = 'CASH_APP_PAY',
}

export enum OrderPaymentResponseStatus {
  PENDING = 'PENDING',
  SUCCESSFUL = 'SUCCESSFUL',
  FAILED = 'FAILED',
  PAYMENT_DECLINED = 'PAYMENT_DECLINED',
}

export interface OrderPaymentResponses {
  type: OrderPaymentResponsesType
  amount: Money
  createdDate: number
  receipt: string
  displayName: string
  includingFees: Money
  paymentSource: PaymentSource
  paymentMethodType: PaymentMethodType
  cardBrand: string
  status: OrderPaymentResponseStatus
}

export enum OrderSource {
  NO_VALUE = 'NO_VALUE',
  API_V0 = 'API_V0',
  SHOPIFY = 'SHOPIFY',
  POS = 'POS',
  API_V1 = 'API_V1',
  TERMINAL = 'TERMINAL',
  API_V2 = 'API_V2',
  POS_CARD = 'POS_CARD',
  VIRTUAL_CARD_ONLINE = 'VIRTUAL_CARD_ONLINE',
  STRIPE = 'STRIPE',
  ADYEN = 'ADYEN',
  CYBERSOURCE = 'CYBERSOURCE',
  POS_CARD_ONLINE = 'POS_CARD_ONLINE',
  SINGLE_USE_CARD = 'SINGLE_USE_CARD',
  AP_MONEY = 'AP_MONEY',
  SHOPIFY_V2 = 'SHOPIFY_V2',
  SQ_ONLINE = 'SQ_ONLINE',
  CASH_APP_API_V2 = 'CASH_APP_API_V2',
  CASH_APP_AP_BUTTON = 'CASH_APP_AP_BUTTON',
  ANYWHERE_CARD = 'ANYWHERE_CARD',
  ANYWHERE_CARD_ONLINE = 'ANYWHERE_CARD_ONLINE',
  CASH_APP_VIRTUAL_CARD = 'CASH_APP_VIRTUAL_CARD',
  CASH_APP_SINGLE_USE_CARD = 'CASH_APP_SINGLE_USE_CARD',
  CASH_APP_SHOPIFY = 'CASH_APP_SHOPIFY',
  CASH_APP_API_V1 = 'CASH_APP_API_V1',
  CITCON = 'CITCON',
  WORLDPAY = 'WORLDPAY',
  OCEANPAYMENT = 'OCEANPAYMENT',
  VERIFONE = 'VERIFONE',
  ALIPAY = 'ALIPAY',
  PPRO = 'PPRO',
  AIRWALLEX = 'AIRWALLEX',
}

export enum OrderChannel {
  ONLINE = 'ONLINE',
  IN_STORE = 'IN_STORE',
  IN_STORE_CARD = 'IN_STORE_CARD',
  IN_STORE_CARD_ONLINE = 'IN_STORE_CARD_ONLINE',
  MONEY = 'MONEY',
}

export interface OrderRequest {
  orderId: string
}

export enum PaymentRefundStatus {
  AUTOMATICALLY_PROCESSED = 'AUTOMATICALLY_PROCESSED',
  FAILED = 'FAILED',
  MANUALLY_PROCESSED = 'MANUALLY_PROCESSED',
}

export interface OrderRefundResponse {
  type: 'PAYMENT' | 'LATE_FEE'
  amount: Money
  createdDate: number
  receipt: string
  cardBrand?: string
  displayName?: string
  paymentMethodType?: PaymentMethodType
  paymentRefundStatus: PaymentRefundStatus
}

export interface OrderFeeResponse {
  amount: Money
  createdDate: number
}

export enum OrderPaymentType {
  'PAYMENT',
  'REFUND',
  'LATE_FEE',
}

export enum DisputeFlowType {
  DEFAULT = 'DEFAULT',
  SUP = 'SUP',
  ALIBABA = 'ALIBABA',
  DECLINE = 'CUSTOM_DECLINE',
  PREZZEE = 'PREZZEE',
}

export enum OrderPpaDocTypes {
  PAYMENT_AUTHORISATION = 'PAYMENT_AUTHORISATION',
  LOAN_AGREEMENT = 'LOAN_AGREEMENT',
  INSTALLMENT_AGREEMENT = 'INSTALLMENT_AGREEMENT',
  FINAL_PAYMENT_SCHEDULE = 'FINAL_PAYMENT_SCHEDULE',
  CREDIT_SCORE_DISCLOSURE = 'CREDIT_SCORE_DISCLOSURE',
  FINAL_LOAN_AGREEMENT = 'FINAL_LOAN_AGREEMENT',
}

export type OrderPaymentData =
  | (OrderPaymentResponses & { dataType: OrderPaymentType.PAYMENT })
  | (OrderRefundResponse & { dataType: OrderPaymentType.REFUND })
  | (OrderFeeResponse & { dataType: OrderPaymentType.LATE_FEE })

export interface OrderPaymentSchedule extends Omit<PaymentSchedule, 'amountWithFees' | 'amountOwed' | 'refundAmount' | 'paymentMethod'> {
  id: number
  owedAmount: Money
  owedFee: Money
  orderFees?: OrderFeeResponse[]
  paymentRefundResponses: OrderRefundResponse[]
  amountPayable: Money
  paymentResponses: OrderPaymentResponses[]
  promoDiscountPaymentResponses: OrderPaymentResponses[]
  originalInstallmentDueDate: string
  inOwingStatus: boolean
  amountAfterRefunds: Money
  amountSummaryDisplayValue: Money
  isInstallmentLastAndOwed?: boolean
}

export interface ItemSummary {
  name: string
  quantity: number
}

export interface Donation {
  campaignName: string
  amount: Money
}

export interface ToggleAutopayResponse {
  autopayDisabled?: boolean
}

export interface OrderResponse {
  id: number
  token: string
  refundedStatus: OrderTransactionRefundStatus
  paymentType: PaymentType
  orderDate: number
  merchant: OrderMerchant
  consumer: OrderConsumer
  autopayDisabled?: boolean
  canBeDisputed: boolean
  disputeFlowType: DisputeFlowType
  clearpayPartiallyMigrated: boolean
  orderDetail: {
    id: number
    billingAddress: ConsumerAddress | null
    shippingAddress: ConsumerAddress | null
    shippingCourier: string | null
    shippingPriority: 'NO_VALUE' | 'STANDARD' | 'EXPRESS' | null
    merchantOrderId: string
    trackingNumber: string | null
    items: ItemSummary[]
  }
  loan: {
    amountFinanced: Money
    monthlyPayment: Money
    payoutAmount: Money
    targetApr: number
    effectiveApr: number
    term: number
    totalCost: Money
    totalInterest: Money
    hasInterestRateChanged: boolean
    currentInterestRateStartDate: string
    currentInterestRate: number
  } | null
  refunds: Array<{
    id: number
    amount: Money
    refundStatus: OrderRefundStatus
    createdDate: number
    updatedDate: number
  }>
  paymentScheduleSet: OrderPaymentSchedule[]
  paymentMethod: SavedPaymentMethod
  source: OrderSource
  store: {
    id: number
    name: string
    address: ConsumerAddress // @TODO: Rename this type to generic Address so it can be reused here
  } | null
  orderSummary: {
    orderTotal: Money
    totalRefunded: Money
    totalRefundedMerchantAmount: Money
    totalRefundedToCard: Money
    totalRefundedToCardFee: Money
    totalAfterRefund: Money
    totalPaid: Money
    totalPaidFee: Money
    totalOwed: Money
    totalOwedFee: Money
    totalCaptured: Money
    openToCapture: Money
    promoDiscount: {
      totalPromoDiscountAmount: Money
    }
    donation: Donation | null
    totalAmountPending: Money
  }
  countryCode: Country
  merchantAmount: Money
  exchangeRate: number | null
  paymentMethodChangeAllowed: boolean
  enabledCardTypes: CardBrand[]
  paymentUpFront: boolean
  preorder: boolean
  channel: OrderChannel
}

export interface OrdersResult {
  id: number
  token: string
  refundedStatus: OrderRefundStatus
  owedAmount: Money
  amountRefunded: Money
  totalAmountAfterRefunds: Money
  totalAmountAfterRefundsAndDiscounts: Money
  orderDate: number
  merchant: OrderMerchant
  consumer: OrderConsumer
  paymentType: PaymentType
  orderDetail: {
    id: number
    orderAmount: Money
    merchantOrderId: string
    shippingCourier: string | null
    shippingCost: Money
    shippingPriority: 'NO_VALUE' | 'STANDARD' | 'EXPRESS' | null
    discount: Money
    discountType: string
    billingAddress: ConsumerAddress
    shippingAddress: ConsumerAddress
    trackingNumber: string | null
  }
  totalAmount: Money
  totalPaid: Money
  source: OrderSource
  channel: OrderChannel
  subscriptionPayment: boolean
  clearpayPartiallyMigrated: boolean
  openRegZDispute: boolean
}

export interface OrdersResponse {
  totalResults: number
  offset: number
  limit: number
  results: OrdersResult[]
}

export interface OrderPpaResponse {
  ppaDocs: Array<{
    docType: OrderPpaDocTypes
    documentId: string
  }>
}

export interface OrderPpaDocumentResponse {
  documentId: string
  renderedHtml: string
}

export interface CreateDisputeQuery {
  orderId: number
  formData: FormData
}

export interface Attachment {
  commentId: number
  createdAt: number
  updatedAt: number
  evidenceLink: string
  expireAt: number
  id: number
  url: string | null
}

export enum Identity {
  Consumer = 'CONSUMER',
  Merchant = 'MERCHANT',
  Admin = 'ADMIN',
}

export enum CommentMark {
  Normal = 'NORMAL',
  Initial = 'INITIAL',
  Close = 'CLOSE',
  AdminOnly = 'ADMIN_ONLY',
}

export interface Comment {
  content: string
  mark: CommentMark
  createdAt: number
  updatedAt: number
  creatorId: number
  disputeCaseId: string
  id: number
  identity: Identity
}

export enum Intent {
  Unknown = 'UNKNOWN',
  ClaimFiled = 'CLAIM_FILED',
  PendingMerchantResponse = 'PENDING_MERCHANT_RESPONSE',
  PendingConsumerResponse = 'PENDING_CONSUMER_RESPONSE',
  PendingRepresentmentReview = 'PENDING_REPRESENTMENT_REVIEW',
  MerchantFavor = 'MERCHANT_FAVOR',
  ConsumerFavor = 'CONSUMER_FAVOR',
  MerchantAccepted = 'MERCHANT_ACCEPTED',
  MerchantResponded = 'MERCHANT_RESPONDED',
  NoMerchantResponse = 'NO_MERCHANT_RESPONSE',
  ConsumerCancelled = 'CONSUMER_CANCELLED',
  PendingTrackingStatus = 'PENDING_TRACKING_STATUS',
  PendingConsumerResubmitEvidence = 'PENDING_CONSUMER_RESUBMIT_EVIDENCE',
}

export interface DisputeEntity {
  amount: string
  currency: string
  comments: Comment[]
  attachments: Attachment[]
  closingNote: string | null
  closingReason: string | null
  id: number
  consumerAccountUuid: string
  orderId: number
  merchantOrderId: string
  consumerId: number
  merchantId: number
  updatedAt: number
  createdAt: number
  reason: string
  status: string
  intent: Intent
  subject: string
  merchantDisplayState: string
  consumerDisplayState: string
  clawback: boolean
}

export interface GetDisputeInfoRequest {
  orderId: string
  type: string
}

export enum DisputePermissionError {
  DuplicatedEntity = 'DUPLICATED_ENTITY',
  ExternalDisputeExisted = 'EXTERNAL_DISPUTE_EXISTED',
  DisputeTimesLimitExceeded = 'DISPUTE_TIMES_LIMIT_EXCEEDED',
  Unknown = 'UNKNOWN',
}

export interface DisputeInfoResponse {
  disputeEntities: DisputeEntity[]
  isDisputePermitted?: boolean
  disputePermissonError?: DisputePermissionError
}

export interface NotifyReturnResponse {
  orderId: number
  body: {
    orderReturned: boolean
    returnDate?: string
    returnReason: string
  }
}

export interface GetChargebackInfoRequest {
  orderId: string
  limit?: number
  offset?: number
}

export enum ChargebackStatus {
  UNKNOWN = 'UNKNOWN',
  WAITING_FOR_RESPONSE = 'WAITING_FOR_RESPONSE',
  OPEN = 'OPEN',
  WIN = 'WIN',
  LOST = 'LOST',
  PRE_CHARGEBACK = 'PRE_CHARGEBACK',
  PRE_CHARGEBACK_OPEN = 'PRE_CHARGEBACK_OPEN',
  COMPLETED = 'COMPLETED',
  AUTO_PROCESS_OPEN = 'AUTO_PROCESS_OPEN',
}

export enum ChargebackSource {
  AMEX = 'amex',
  WORLDPAY = 'worldpay',
  STRIPE = 'stripe',
  ADYEN = 'adyen',
}

export enum ChargebackType {
  OTHER = 'OTHER',
  MERCHANDISE = 'MERCHANDISE',
  FRAUD = 'FRAUD',
  UNKNOWN = 'UNKNOWN',
}

export interface ChargebackDetails {
  reason: string
  source: ChargebackSource
  type: ChargebackType
  consumerAccountResponse?: {
    dateOfBirth: string
    email: string
    emailVerified: boolean
    givenNames: string
    mobile: string
    otherGiveNames?: string
    surname: string
    verifiedMobile: string
    verifiedMobileDate: number
  }
  merchantResponse?: {
    companyName: string
    contactEmail: string
    contactNumber: string
    contactUrl?: string
    countryCode: string
    detailsUrl: string
    id: number
    merchantDisplayName?: string
    tradingName: string
  }
}

export interface ChargebackEvent {
  status: ChargebackStatus
  eventType: string
  disputeServiceUpdateDatetime: number
  actorId: number
  actorName: string
  comment: string
}

export enum ChargebackIntegrationType {
  NO_VALUE = 'NO_VALUE',
  API_V0 = 'API_V0',
  SHOPIFY = 'SHOPIFY',
  POS = 'POS',
  API_V1 = 'API_V1',
  TERMINAL = 'TERMINAL',
  API_V2 = 'API_V2',
  POS_CARD = 'POS_CARD',
  VIRTUAL_CARD_ONLINE = 'VIRTUAL_CARD_ONLINE',
  STRIPE = 'STRIPE',
  ADYEN = 'ADYEN',
  CYBERSOURCE = 'CYBERSOURCE',
  POS_CARD_ONLINE = 'POS_CARD_ONLINE',
  SINGLE_USE_CARD = 'SINGLE_USE_CARD',
  AP_MONEY = 'AP_MONEY',
  SHOPIFY_V2 = 'SHOPIFY_V2',
  SQ_ONLINE = 'SQ_ONLINE',
  CASH_APP_API_V2 = 'CASH_APP_API_V2',
  CASH_APP_AP_BUTTON = 'CASH_APP_AP_BUTTON',
  CITCON = 'CITCON',
  WORLDPAY = 'WORLDPAY',
  OCEANPAYMENT = 'OCEANPAYMENT',
  VERIFONE = 'VERIFONE',
  ALIPAY = 'ALIPAY',
  PPRO = 'PPRO',
  AIRWALLEX = 'AIRWALLEX',
}

export interface Chargeback {
  acquirerChargebackId: string
  acquirerUrl: string
  amount: Money
  consumerAccountUuid: string
  countryCode: Country
  details: ChargebackDetails
  dueDate: number
  events: ChargebackEvent[]
  id: number
  installmentIds: number[]
  integrationType: ChargebackIntegrationType
  maskedPan: string
  merchantId: number
  orderDate: number
  orderId: number
  paymentId: number
  receivedDate: number
  source: ChargebackSource
  status: ChargebackStatus
  topazId: string
  isResolved: boolean
}

export interface GetChargebackInfoResponse {
  limit: number
  offset: number
  results: Chargeback[]
  totalResults: number
}
